import type { UploadedFile, UploadPort } from "@/shared/contracts/ports";
import { request } from "@/shared/http/client";
import { postUpload } from "./use-files";

/**
 * The upload capability, implemented where HTTP is allowed.
 *
 * Widgets receive this through provide/inject rather than importing it: only a
 * feature's own `api` folder may speak HTTP, and a component that calls `fetch`
 * is how one contract ends up written in three places.
 */
export function createUploadPort(): UploadPort {
	// Resolved files are cached: a form with six attachments would otherwise ask
	// for six URLs again on every re-render.
	const resolved = new Map<string, UploadedFile>();

	return {
		async upload(input): Promise<UploadedFile> {
			// Multipart: base64 in JSON stopped at the server's 20 MB body limit.
			// See `postUpload`.
			const res = await postUpload({
				blob: input.blob,
				filename: input.filename,
				ownerKind: input.ownerKind,
				...(input.ownerId ? { ownerId: input.ownerId } : {}),
				purpose: input.purpose,
			});

			const stored = describe(res);
			resolved.set(stored.id, stored);
			return stored;
		},

		async urlFor(fileId: string, opts): Promise<UploadedFile> {
			// A URL de baixar e a de abrir são DUAS respostas do servidor para o mesmo
			// arquivo — no driver de objeto elas têm assinaturas diferentes —, então a
			// chave do cache carrega o pedido junto. Sem isso, quem abrisse primeiro
			// faria o botão de baixar entregar a URL de abrir.
			const download = opts?.download === true;
			const key = download ? `${fileId}?download` : fileId;
			const cached = resolved.get(key);
			if (cached) return cached;
			const res = await request<{
				file: { id: string; path: string; mimeType: string; sizeBytes: number };
				url: string;
			}>(`/v3/files/${fileId}/url${download ? "?download=1" : ""}`);
			const stored = describe(res);
			resolved.set(key, stored);
			return stored;
		},
	};
}

/** As duas rotas respondem a mesma dupla `{file, url}`; uma leitura só para as duas. */
function describe(res: {
	file: { id: string; path: string; mimeType: string; sizeBytes: number };
	url: string;
}): UploadedFile {
	return {
		id: res.file.id,
		url: res.url,
		mimeType: res.file.mimeType,
		sizeBytes: res.file.sizeBytes,
		fileName: res.file.path.split("/").pop() || "arquivo",
	};
}
